// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Box, Button, Group, Menu, Stack, Tabs, Text, Textarea, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Appointment, Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCalendarPlus, IconDots, IconMessageCircle } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { cancelProviderAppointment } from '../../services/appointment-lifecycle-service';
import {
  getAppointmentTypeDisplay,
  getPatientFromAppointment,
  isCheckInAppointment,
} from '../../utils/appointment-formatting';
import { getVisitOutcomeLabel } from '../../utils/encounter';
import { showErrorNotification } from '../../utils/notifications';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { NewTopicDialog } from '../messages/NewTopicDialog';
import { PatientVitalsSummary } from '../patient/PatientVitalsSummary';
import { AppointmentCancellationModal } from '../schedule/AppointmentCancellationModal';
import styles from './EncounterNoteRefactoredShell.module.css';

const BRAND = '#640040';

type EncounterWorkspaceTab = 'profile' | 'medical-info' | 'encounter-note' | 'coordinator-note';

interface EncounterWorkspaceShellProps {
  appointment: Appointment | undefined;
  canSchedule: boolean;
  children: ReactNode;
  currentTab: EncounterWorkspaceTab;
  encounter: Encounter;
  onConclude: (() => void) | undefined;
  onOpenSchedule: (() => void) | undefined;
  patient: Patient;
}

function getPatientDisplayName(patient: Patient): string {
  const officialName = patient.name?.[0];
  const given = officialName?.given?.join(' ')?.trim();
  const family = officialName?.family?.trim();
  return [given, family].filter(Boolean).join(' ') || officialName?.text || 'Patient';
}

function getEncounterTypeDisplay(encounter: Encounter): string {
  const coding = encounter.type?.[0]?.coding?.[0];
  return encounter.type?.[0]?.text ?? coding?.display ?? coding?.code ?? 'Encounter';
}

function isCheckInEncounter(encounter: Encounter): boolean {
  return (
    encounter.type?.some((type) =>
      type.coding?.some(
        (coding) => coding.code?.toLowerCase() === 'check-in' || coding.display?.toLowerCase() === 'check-in'
      )
    ) ?? false
  );
}

function isCoordinatorNoteOnly(encounter: Encounter, appointment: Appointment | undefined): boolean {
  return isCheckInEncounter(encounter) || (appointment ? isCheckInAppointment(appointment) : false);
}

export function EncounterWorkspaceShell({
  appointment,
  canSchedule,
  children,
  currentTab,
  encounter,
  onConclude,
  onOpenSchedule,
  patient,
}: EncounterWorkspaceShellProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [cancelOpened, { open: openCancel, close: closeCancel }] = useDisclosure(false);
  const [messageOpened, { open: openMessage, close: closeMessage }] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  // A visit that is already over shows why instead of a dead Conclude button.
  const visitOutcomeLabel = getVisitOutcomeLabel(encounter, appointment);
  const coordinatorNoteOnly = isCoordinatorNoteOnly(encounter, appointment);
  const appointmentTitle = (() => {
    if (currentTab === 'coordinator-note') {
      return `Coordinator Note for ${getPatientDisplayName(patient)}`;
    }
    if (appointment) {
      return `${getAppointmentTypeDisplay(appointment)} with ${getPatientFromAppointment(appointment).name}`;
    }
    return `${getEncounterTypeDisplay(encounter)} with ${getPatientDisplayName(patient)}`;
  })();

  async function handleCancelAppointment(): Promise<void> {
    if (!appointment?.id) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelProviderAppointment(medplum, appointment.id, cancelReason.trim() || undefined);
      closeCancel();
      setCancelReason('');
      navigate(`/Patient/${patient.id}/Appointment`);
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setIsCancelling(false);
    }
  }

  const handleTabChange = useCallback(
    (value: string | null): void => {
      if (!patient.id || !encounter.id || !value) {
        return;
      }

      const basePath = `/Patient/${patient.id}/Encounter/${encounter.id}`;
      let nextPath = basePath;

      if (value === 'profile') {
        nextPath = `${basePath}/profile`;
      } else if (value === 'medical-info') {
        nextPath = `${basePath}/medical-info`;
      } else if (value === 'coordinator-note') {
        nextPath = `${basePath}/coordinator-note`;
      }

      navigate(nextPath);
    },
    [encounter.id, navigate, patient.id]
  );

  return (
    <Box className={styles.shell}>
      <Stack className={styles.page} gap="xl">
        <Stack className={styles.hero} gap="lg">
          <Stack gap={6}>
            <Text c={BRAND} fw={700} size="sm">
              Appointments &gt;
            </Text>
          </Stack>

          <div className={styles.heroRail}>
            <div className={styles.heroTitleRow}>
              <Title className={styles.heroTitle} order={1}>
                {appointmentTitle}
              </Title>
              {appointment?.start && appointment?.end && (
                <AppointmentTimeWithTimezone
                  start={appointment.start}
                  end={appointment.end}
                  textProps={{ c: 'dimmed', fw: 500, size: 'lg' }}
                />
              )}
            </div>
            <div className={styles.heroControls}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="pills"
                radius="md"
                classNames={{
                  root: styles.encounterTabs,
                  list: styles.encounterTabsList,
                  tab: styles.encounterTab,
                }}
              >
                <Tabs.List>
                  <Tabs.Tab value="profile">Profile</Tabs.Tab>
                  <Tabs.Tab value="medical-info">Medical Info</Tabs.Tab>
                  {!coordinatorNoteOnly && <Tabs.Tab value="encounter-note">Encounter Note</Tabs.Tab>}
                  {coordinatorNoteOnly && <Tabs.Tab value="coordinator-note">Coordinator Note</Tabs.Tab>}
                </Tabs.List>
              </Tabs>
              <div className={styles.heroActions}>
                <Button
                  className={styles.heroActionButton}
                  onClick={openMessage}
                  rightSection={<IconMessageCircle size={18} />}
                >
                  Message
                </Button>
                <Menu position="bottom-end" shadow="md" withinPortal>
                  <Menu.Target>
                    <Button className={styles.heroIconButton} variant="default" aria-label="More actions">
                      <IconDots size={20} />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      disabled={!appointment?.id || !canSchedule}
                      leftSection={<IconCalendarPlus size={16} />}
                      onClick={() => {
                        if (appointment?.id) {
                          navigate(`/Patient/${patient.id}/Appointment/reschedule/${appointment.id}`);
                        }
                      }}
                    >
                      Reschedule Appointment
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      disabled={!appointment?.id || appointment.status !== 'booked' || !canSchedule}
                      onClick={openCancel}
                    >
                      Cancel Appointment
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
                {canSchedule && (
                  <Button
                    className={styles.heroActionButton}
                    leftSection={<IconCalendarPlus size={16} />}
                    onClick={onOpenSchedule}
                  >
                    Schedule Next Appointment
                  </Button>
                )}
                {visitOutcomeLabel ? (
                  <Badge size="lg" variant="light" color={BRAND} aria-label={`Visit ${visitOutcomeLabel}`}>
                    {visitOutcomeLabel}
                  </Badge>
                ) : (
                  <Button className={styles.heroPrimaryButton} onClick={onConclude}>
                    Conclude Visit
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Stack>

        <PatientVitalsSummary patient={patient} variant="encounter" />

        {children}
      </Stack>

      <AppointmentCancellationModal
        opened={cancelOpened}
        onClose={closeCancel}
        onConfirm={handleCancelAppointment}
        loading={isCancelling}
        reasonInput={
          <Textarea
            label="Reason (optional)"
            placeholder="Why is this being cancelled?"
            value={cancelReason}
            onChange={(event) => setCancelReason(event.currentTarget.value)}
          />
        }
      />

      <NewTopicDialog opened={messageOpened} subject={{ reference: `Patient/${patient.id}` }} onClose={closeMessage} />
    </Box>
  );
}
